import {Button, Checkbox, Modal, Radio, Spin} from 'antd'
import {docs_fetchCodiDocumentRows_Api, docs_fetchCodiDocuments_Api,} from 'apis'
import {getId} from 'apis/model/base'
import classNames from 'classnames'
import EmptyHolder from 'components/EmptyHolder'
import {createValue} from 'components/form/utils'
import {ROOT_URL} from 'envs/_current/config'
import {Formik} from 'formik'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {PiAt} from 'react-icons/pi'
import {Provider} from 'react-redux'
import {SelectWidget} from 'views/AppLocals/custom/ReactSelectComponents'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import {NullAsDefault, renderIf} from 'views/Shared'
import {search_searchObjects_Api} from '../../../apis'
import {codi_content_options} from '../../../views/SlateEditor/codi'

export const CodiSelectorActions = Object.freeze({
    EXIT: 'exit',
    CONTENT: 'content',
    OTHERS: 'others',
    CODI: 'codi-document',
})

export const ObjectSelect = ({timeout = 300, type, ...props}) => {
    const t = useTranslate()

    const [options, setOptions] = useState([])

    const [fetching, setFetching] = useState(false)

    const {isLoading = false, handleAsyncAction: fetchData = NullAsDefault} =
        useAsyncAction({
            apiInfo: search_searchObjects_Api,
            onError: notifyOnError(t),
            onSuccess: (__, data) => {
                const newOptions = _.get(data, 'response.data.data', [])
                setOptions(transformOptions(newOptions))
                setFetching(false)
            },
        })

    const transformOptions = (options = []) => {
        return Array.from(options).map(({title, description, ...item}) => ({
            ...item,
            title,
            label: (
                <div className="flex flex-col">
                    <div className="flex items-center gap-1 font-bold text-xs italic">
                        <span className="text-primary truncate">{title}</span>
                    </div>
                </div>
            ),
            value: getId(item),
        }))
    }

    useEffect(() => {
        fetchData()
    }, [])

    const debounceFetcher = useMemo(() => {
        const loadOptions = (keyword) => {
            setOptions([])
            setFetching(true)
            const attachParams = type ? {type} : {}
            fetchData({keyword, ...attachParams})
        }
        return _.debounce(loadOptions, timeout)
    }, [timeout])
    return (
        <SelectWidget
            allowClear={true}
            showSearch={true}
            labelInValue={true}
            filterOption={false}
            loading={!!isLoading}
            optionLabelProp="title"
            onSearch={debounceFetcher}
            notFoundContent={fetching ? <Spin size="small"/> : <EmptyHolder/>}
            {...props}
            options={isLoading ? [] : options}
        />
    )
}

export const CodiDocumentSelect = ({timeout = 300, ...props}) => {
    const t = useTranslate()

    const [options, setOptions] = useState([])

    const [fetching, setFetching] = useState(false)

    const {isLoading = false, handleAsyncAction: fetchData = NullAsDefault} =
        useAsyncAction({
            apiInfo: docs_fetchCodiDocuments_Api,
            onError: notifyOnError(t),
            onSuccess: (__, data) => {
                const newOptions = _.get(data, 'response.data.data', [])
                setOptions(transformOptions(newOptions))
                setFetching(false)
            },
        })

    const transformOptions = (options = []) => {
        return Array.from(options).map(
            ({title, description, document, ...item}) => ({
                ...item,
                title,
                label: (
                    <div className="flex flex-col">
                        <div className="flex items-center gap-1 font-bold text-xs italic">
              <span className="text-primary truncate">
                {title}
                  {renderIf(
                      _.get(item, 'document_no'),
                      <span className="font-medium text-xs text-color-400 ml-2">
                      {`(${_.get(item, 'document_no')})`}
                    </span>
                  )}
              </span>
                        </div>
                        <div className="flex items-center gap-1 italic leading-tight">
              <span className="font-medium text-2xs text-color-100">
                {_.get(item, 'document_type.title')}
              </span>
                        </div>
                    </div>
                ),
                value: getId(item),
            })
        )
    }

    useEffect(() => {
        fetchData()
    }, [])

    const debounceFetcher = useMemo(() => {
        const loadOptions = (keyword) => {
            setOptions([])
            setFetching(true)
            fetchData({keyword})
        }
        return _.debounce(loadOptions, timeout)
    }, [timeout])
    return (
        <SelectWidget
            allowClear={true}
            showSearch={true}
            labelInValue={true}
            filterOption={false}
            loading={!!isLoading}
            optionLabelProp="title"
            onSearch={debounceFetcher}
            notFoundContent={fetching ? <Spin size="small"/> : <EmptyHolder/>}
            {...props}
            options={isLoading ? [] : options}
        />
    )
}

export const CodiSelect = ({timeout = 300, includes = [], ...props}) => {
    const t = useTranslate()

    const [options, setOptions] = useState([])

    const [fetching, setFetching] = useState(false)

    const {isLoading = false, handleAsyncAction: fetchData = NullAsDefault} =
        useAsyncAction({
            apiInfo: docs_fetchCodiDocuments_Api,
            onError: notifyOnError(t),
            onSuccess: (__, data) => {
                const newOptions = _.get(data, 'response.data.data', [])
                setOptions(transformOptions(newOptions))
                setFetching(false)
            },
        })

    const transformOptions = (options = []) =>
        Array.from(options).map(({title, description, document, ...item}) => ({
            ...item,
            title,
            value: getId(item),
            label: (
                <div className="flex flex-col">
                    <div className="flex items-baseline gap-1 font-bold text-xs text-color-000 truncate">
                        {title}
                        {renderIf(
                            item?.document_no,
                            <span className="font-medium text-2xs text-primary-500 italic tracking-wider">
                                {`(${item?.document_no})`}
                            </span>
                        )}
                    </div>
                    <div className="text-2xs text-color-300 italic tracking-wider leading-tight">
                        {getTitle(item?.document_type)}
                    </div>
                </div>
            ),
        }))

    useEffect(() => {
        fetchData({includes: includes.join(',')})
    }, [])

    const debounceFetcher = useMemo(() => {
        const loadOptions = (keyword) => {
            setOptions([])
            setFetching(true)
            fetchData({keyword, includes: includes.join(',')})
        }
        return _.debounce(loadOptions, timeout)
    }, [timeout])

    return (
        <SelectWidget
            allowClear={true}
            showSearch={true}
            labelInValue={true}
            filterOption={false}
            loading={!!isLoading}
            optionLabelProp="title"
            onSearch={debounceFetcher}
            notFoundContent={fetching ? <Spin size="small"/> : <EmptyHolder/>}
            {...props}
            options={isLoading ? [] : options}
        />
    )
}

export const CodiDocumentRowSelect = ({
                                          codi_document_id,
                                          timeout = 300,
                                          children,
                                          ...props
                                      }) => {
    const t = useTranslate()

    const [options, setOptions] = useState([])

    const [fetching, setFetching] = useState(false)

    const transformOptions = (options = []) =>
        Array.from(options).map(
            ({index_cd, index_name, short_index_name, content, ...item}) => {
                const prefix = short_index_name || index_cd || index_name
                return {
                    content,
                    index_name,
                    short_index_name,
                    title: prefix
                        ? content
                            ? [prefix, content].join(': ')
                            : prefix
                        : content,
                    label: (
                        <div className="space-x-1 truncate custom-select-mention">
                            {renderIf(
                                prefix,
                                <span className="font-semibold text-color-000 text-sm">
                                    {`${prefix}: `}
                                </span>
                            )}
                            {renderIf(
                                content,
                                <span className="text-xs italic text-color-300 tracking-wider leading-tight truncate">
                                    {content}
                                </span>
                            )}
                        </div>
                    ),
                    ...item,
                    value: getId(item),
                }
            }
        )

    const {isLoading = false, handleAsyncAction: fetchData = NullAsDefault} =
        useAsyncAction({
            onError: notifyOnError(t),
            apiInfo: docs_fetchCodiDocumentRows_Api,
            query: bindQueryParam({id: codi_document_id}),
            onSuccess: (__, data) => {
                const newOptions = _.get(data, 'response.data.data', [])
                setOptions(transformOptions(newOptions))
                setFetching(false)
            },
        })

    useEffect(() => {
        fetchData({}, {id: codi_document_id})
    }, [codi_document_id])

    const debounceFetcher = useMemo(() => {
        const loadOptions = (keyword) => {
            setOptions([])
            setFetching(true)
            fetchData({keyword}, {id: codi_document_id})
        }
        return _.debounce(loadOptions, timeout)
    }, [timeout, codi_document_id])

    if (_.isEmpty(codi_document_id)) {
        return null
    }

    return (
        <React.Fragment>
            <SelectWidget
                key={codi_document_id}
                allowClear={true}
                showSearch={true}
                labelInValue={true}
                filterOption={false}
                loading={!!isLoading}
                optionLabelProp="title"
                onSearch={debounceFetcher}
                notFoundContent={fetching ? <Spin size="small"/> : <EmptyHolder/>}
                {...props}
                options={options}
            />
            {children}
        </React.Fragment>
    )
}

function withPath(path) {
    return path ? `${ROOT_URL}${path}` : ROOT_URL
}

function withContent(content, title) {
    return content + (String(title).length > 0 ? ' - ' : '')
}

export const useInputUtils = ({
                                  store,
                                  focus = NullAsDefault,
                                  handleSubmit = NullAsDefault,
                                  hasSelection = false,
                              }) => {
    const t = useTranslate()

    const action = useRef()

    const getPreview = ({
                            name_option,
                            codi_document,
                            display_options,
                            codi_document_row,
                        }) => {
        let content
        let link_array = []
        let string_array = []

        const target = codi_document_row ?? codi_document

        const show_content = display_options.includes(
            codi_content_options.content.value
        )

        const display_name = name_option === codi_content_options.name.value

        const display_short_name =
            name_option === codi_content_options.short_name.value

        if (show_content) {
            if (codi_document_row) {
                content = codi_document_row?.content
            }
        }

        if (codi_document) {
            if (display_short_name) {
                link_array.push(codi_document?.document_no)
            } else if (display_name) {
                link_array.push(codi_document?.title)
            }
        }

        if (codi_document_row) {
            if (display_short_name) {
                link_array.push(codi_document_row?.short_index_name)
                string_array.push(codi_document_row?.short_index_name)
            } else if (display_name) {
                link_array.push(codi_document_row?.index_name)
                string_array.push(codi_document_row?.index_name)
            }
        }

        const filtered_link_array = link_array.filter(
            (e) => e && String(e).trim().length
        )

        const filtered_string_array = string_array.filter(
            (e) => e && String(e).trim().length
        )

        return {
            content,
            link: filtered_link_array.length
                ? filtered_link_array.join(filtered_link_array?.length > 1 ? ' - ' : '')
                : getTitle(target),
            title: filtered_string_array.length
                ? filtered_string_array.join(
                    filtered_string_array?.length > 1 ? ' - ' : ''
                )
                : getTitle(target),
        }
    }

    const onAddCodiMention = () => {
        if (!!!hasSelection) return
        Modal.destroyAll()
        Modal.confirm({
            className: 'NoFooter custom-modal',
            width: 600,
            icon: null,
            title: (
                <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                    {t('insert book content')}
                </div>
            ),
            content: (
                <Provider store={store}>
                    <div className="flex flex-col gap-4 mt-6">
                        <Formik
                            enableReinitialize={true}
                            initialValues={{
                                display_options: [],
                                codi_document: undefined,
                                codi_document_row: undefined,
                                name_option: codi_content_options.name.value,
                            }}>
                            {({values, handleChange, setFieldValue}) => {
                                const {
                                    name_option,
                                    codi_document,
                                    display_options,
                                    codi_document_row,
                                } = values ?? {}

                                const preview = getPreview({
                                    name_option,
                                    codi_document,
                                    display_options,
                                    codi_document_row,
                                })

                                return (
                                    <React.Fragment>
                                        <div className="flex flex-col gap-1">
                      <span className="text-xs text-color-300 italic">
                        {t('choose book')}
                      </span>
                                            <CodiDocumentSelect
                                                placeholder={t('book title')}
                                                onChange={(item, option) => {
                                                    setFieldValue('codi_document_row', undefined)
                                                    handleChange(createValue('codi_document', option))
                                                }}
                                            />
                                        </div>
                                        {renderIf(
                                            codi_document,
                                            <div className="flex flex-col gap-2">
                        <span className="text-xs text-color-300 italic">
                          {t('choose chapter sentence')}
                        </span>
                                                <CodiDocumentRowSelect
                                                    placeholder={t('detail')}
                                                    codi_document_id={getId(codi_document)}
                                                    onChange={(item, option) => {
                                                        handleChange(
                                                            createValue('codi_document_row', option)
                                                        )
                                                    }}>
                                                    <div className="flex flex-col ml-1 mt-3">
                                                        <div
                                                            className="text-sm text-color-300 italic font-medium underline mb-1">
                                                            {t('display options')}
                                                        </div>
                                                        <Radio.Group
                                                            name="name_option"
                                                            onChange={handleChange}
                                                            defaultValue={name_option}
                                                            className="flex items-center gap-3">
                                                            {[
                                                                codi_content_options.name,
                                                                codi_content_options.short_name,
                                                            ].map(({label, value}, index) => (
                                                                <Radio
                                                                    className="py-1 text-sm text-color-300 italic"
                                                                    key={index}
                                                                    label={t(label)}
                                                                    value={value}>
                                                                    {t(label)}
                                                                </Radio>
                                                            ))}
                                                        </Radio.Group>
                                                        {renderIf(
                                                            codi_document_row,
                                                            <Checkbox.Group
                                                                name="display_options"
                                                                className="flex items-center py-1 gap-1"
                                                                defaultValue={display_options}
                                                                onChange={(checked_values) => {
                                                                    handleChange(
                                                                        createValue(
                                                                            'display_options',
                                                                            checked_values
                                                                        )
                                                                    )
                                                                }}>
                                                                {[codi_content_options.content].map(
                                                                    ({label, value}, index) => (
                                                                        <Checkbox
                                                                            key={index}
                                                                            value={value}
                                                                            label={t(label)}
                                                                            className="py-1 text-sm text-color-300 italic">
                                                                            {t(label)}
                                                                        </Checkbox>
                                                                    )
                                                                )}
                                                            </Checkbox.Group>
                                                        )}
                                                    </div>
                                                </CodiDocumentRowSelect>
                                                <div className="flex flex-col gap-2 mt-1">
                                                    <div className="border-b text-sm text-color-200 italic font-medium">
                                                        {t('in preview3')}
                                                    </div>
                                                    <div className="flex flex-col px-2">
                            <span>
                              {renderIf(
                                  preview?.content,
                                  <span className="italic">
                                  {preview?.content +
                                  (String(preview?.link).length > 0 ? ' - ' : '')}
                                </span>
                              )}
                                <span className="text-primary">
                                {preview?.link}
                              </span>
                            </span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        <div
                                            style={{marginTop: 24}}
                                            className="flex gap-3">
                                            <div className="flex-1"/>
                                            <Button
                                                type="default"
                                                onClick={() => {
                                                    Modal.destroyAll()
                                                    focus()
                                                }}
                                                className="button-rounded-lg no-border">
                                                {t('cancel')}
                                            </Button>
                                            <Button
                                                type="primary"
                                                onClick={() => {
                                                    const owner = codi_document_row ?? codi_document
                                                    handleSubmit(action.current, {
                                                        owner,
                                                        link: preview?.link,
                                                        title: preview?.title,
                                                        content: preview?.content,
                                                    })
                                                    Modal.destroyAll()
                                                }}
                                                disabled={_.isEmpty(values)}
                                                className="button-rounded-lg no-border">
                                                {t('add content')}
                                            </Button>
                                        </div>
                                    </React.Fragment>
                                )
                            }}
                        </Formik>
                    </div>
                </Provider>
            ),
        })
    }

    const actions = useMemo(
        () => [
            {
                type: 'primary',
                label: 'codi document',
                icon: (
                    <PiAt
                        size={23}
                        className={classNames(
                            hasSelection
                                ? 'text-color-300 hover:text-color-100 cursor-pointer'
                                : 'text-primary'
                        )}
                    />
                ),
                value: CodiSelectorActions.CODI,
                disabled: !!!hasSelection,
                onItemClick: (value) => {
                    action.current = value
                    onAddCodiMention()
                },
            },
        ],
        [hasSelection]
    )

    const renderActions = useCallback(
        ({style, className, showLabel = true}) => (
            <div
                style={style}
                className={classNames('flex gap-2 items-center', className)}>
                {actions.map(
                    ({value, label, icon, onItemClick = NullAsDefault, ...rest}, index) => (
                        <Button
                            key={index}
                            icon={icon}
                            onClick={() => {
                                onItemClick(value)
                            }}
                            style={{background: 'transparent', padding: 0}}
                            className="button-rounded-full no-border flex flex-center"
                            {...rest}>
                            {showLabel && t(label)}
                        </Button>
                    )
                )}
            </div>
        ),
        [actions]
    )

    return {
        action,
        actions,
        renderActions,
    }
}
